import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { matchIsValidTel } from 'mui-tel-input';
import { useNotify } from '@/components/snackbar';
import { useAuthContext } from '@/auth/useAuthContext';
import {
  validateEmail,
  validateWhatsapp,
  requestUserEmailToken,
  requestUserWhatsappToken,
  updateUserService,
} from '@/services/user';
import { whatsappValidationEnabled } from '@/consts/auth';
import useTimeCounter from '@/hooks/useTimeCounter';
import { validationSchema } from './validations';

const EMAIL_RESEND_TIME_SECONDS = 60;

export default function useVerifyModal({
  onSuccessWhatsapp = () => {},
  isEmailVerificationOpen = false,
}) {
  const [emailError, setEmailError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [requestEmailTokenSuccessMessage, setRequestEmailTokenSuccess] = useState('');
  const [requestWhatsappSuccessMessage, setRequestWhatsappSuccessMessage] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState();
  const [emailResendTimeLeft, startEmailResendCountdown, stopEmailResendCountdown] = useTimeCounter(
    EMAIL_RESEND_TIME_SECONDS,
    false
  );
  const isRequestingEmailTokenRef = useRef(false);

  const { userCellphone } = useAuthContext();

  const notify = useNotify();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { isLoading: isLoadingRequestEmailToken, mutate: requestEmailTokenMutation } = useMutation({
    onMutate() {
      setEmailError('');
      setRequestEmailTokenSuccess('');
    },
    mutationFn: requestUserEmailToken,
    onSuccess(result) {
      setRequestEmailTokenSuccess(result?.data?.detail);
      startEmailResendCountdown();
    },
    onError(error) {
      const detail = error?.response?.data?.detail;

      if (!detail) {
        setEmailError('Ocorreu um erro ao solicitar o código. Tente novamente.');
        return;
      }

      const rateLimitMatch = detail.match(
        /^Pedido foi limitado\. Expected available in (\d+) seconds\.$/
      );

      if (rateLimitMatch) {
        startEmailResendCountdown(Number(rateLimitMatch[1]));
      }

      const translatedMessage = detail.replace(
        /^Pedido foi limitado\. Expected available in (\d+) seconds\.$/,
        'Pedido foi limitado. Estará disponível novamente em $1 segundos.'
      );
      setEmailError(translatedMessage);
    },
    onSettled() {
      isRequestingEmailTokenRef.current = false;
    },
  });

  const canResendEmailToken = isEmailVerificationOpen && emailResendTimeLeft === 0;

  const requestEmailToken = () => {
    if (!canResendEmailToken || isLoadingRequestEmailToken || isRequestingEmailTokenRef.current) {
      return;
    }

    isRequestingEmailTokenRef.current = true;
    requestEmailTokenMutation();
  };

  const { isLoading: isloadingRequestWhatsappToken, mutate: requestWhatsappToken } = useMutation({
    onMutate() {
      setWhatsappError('');
      setRequestWhatsappSuccessMessage('');
    },
    mutationFn: requestUserWhatsappToken,
    onSuccess(result) {
      setRequestWhatsappSuccessMessage(result?.data?.detail);
    },
    onError(error) {
      const detail = error?.response?.data?.detail;

      if (!detail) {
        setWhatsappError('Ocorreu um erro ao solicitar o código. Tente novamente.');
        return;
      }

      const translatedMessage = detail.replace(
        /^Pedido foi limitado\. Expected available in (\d+) seconds\.$/,
        'Pedido foi limitado. Estará disponível novamente em $1 segundos.'
      );
      setWhatsappError(translatedMessage);
    },
  });

  const { isLoading: isLoadingValidateEmail, mutate: mutateValidateEmail } = useMutation({
    onMutate() {
      setEmailError('');
    },
    mutationFn: validateEmail,
    onSuccess() {
      const isAlsoValidatingWhatsapp = methods.getValues('isToValidateWhatsapp');
      if (!whatsappValidationEnabled || !isAlsoValidatingWhatsapp) {
        onSuccessWhatsapp();
      }
    },
    onError(error) {
      setEmailError(error?.response?.data?.detail || 'Código inválido ou expirado.');
    },
  });

  const { isLoading: isLoadingValidateWhatsapp, mutate: mutateValidateWhatsapp } = useMutation({
    onMutate() {
      setWhatsappError('');
    },
    mutationFn: validateWhatsapp,
    onSuccess() {
      onSuccessWhatsapp();
    },
    onError(error) {
      setWhatsappError(error?.response?.data?.detail || 'Código inválido ou expirado.');
    },
  });

  const onSubmit = (values) => {
    const { isToValidateEmail, isToValidateWhatsapp } = values;

    if (isToValidateEmail) {
      const { email1, email2, email3, email4, email5, email6 } = values;
      const token = `${email1}${email2}${email3}${email4}${email5}${email6}`;
      mutateValidateEmail(token);
    }

    if (isToValidateWhatsapp) {
      const { whatsapp1, whatsapp2, whatsapp3, whatsapp4, whatsapp5, whatsapp6 } = values;
      const token = `${whatsapp1}${whatsapp2}${whatsapp3}${whatsapp4}${whatsapp5}${whatsapp6}`;
      mutateValidateWhatsapp(token);
    }
  };

  const updateCellphone = async () => {
    const phoneCountryCode = methods.getValues('phoneCountryCode');
    const cellphone = methods.getValues('cellphone');

    const isValidPhone = matchIsValidTel(`+${phoneCountryCode}${cellphone}`);

    if (isValidPhone) {
      try {
        await updateUserService({ phoneCountryCode, cellphone });
        setWhatsappNumber(cellphone);
      } catch (error) {
        methods.setError('cellphone', { message: error?.message });
      }
    } else {
      methods.setError('cellphone', { message: 'Número de telefone inválido' });
    }
  };

  const { mutateAsync: updadeCellphoneMutate, isLoading: isLoadingUpdateCellphone } = useMutation({
    mutationFn: updateCellphone,
    onSuccess() {
      notify.success('Número de whatdsapp atualizado com sucesso!');
    },
    onError() {
      notify.error('Ocorreu um erro ao tentar atualizar o número de whatsapp!');
    },
  });

  useEffect(() => {
    setWhatsappNumber(userCellphone);
  }, [userCellphone]);

  useLayoutEffect(() => {
    if (isEmailVerificationOpen) {
      startEmailResendCountdown();
    } else {
      stopEmailResendCountdown();
    }
  }, [isEmailVerificationOpen, startEmailResendCountdown, stopEmailResendCountdown]);

  return {
    methods,
    onSubmit: methods.handleSubmit(onSubmit),
    isLoading:
      isLoadingValidateEmail ||
      isLoadingValidateWhatsapp ||
      isLoadingRequestEmailToken ||
      isloadingRequestWhatsappToken,
    emailError,
    whatsappError,
    requestEmailToken,
    emailResendTimeLeft,
    canResendEmailToken,
    isLoadingRequestEmailToken,
    requestWhatsappToken,
    requestEmailTokenSuccessMessage,
    requestWhatsappSuccessMessage,
    updadeCellphoneMutate,
    isLoadingUpdateCellphone,
    whatsappNumber,
  };
}
